import { Box, GridLegacy as Grid, SxProps, useTheme, Zoom } from '@mui/material';

import React, { FC, useEffect } from 'react';
import { Film } from '@/logic/services';
import InfiniteScroll from 'react-infinite-scroll-component';

import { pxToRem } from '@/theme/typography';
import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';
import {
  EventTypes,
  MovieItemModel,
  SelectItemModel,
  ViewItemListModel,
} from '@/logic/types/data-layer-models';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import { useCity } from '@/logic/hooks';
import { Poster } from './poster';

interface MoviesListProps {
  moviesList: Film[];
  pagination?: { limit: number; page: number };
  fetchMovies: () => void;
  sx?: SxProps;
  hasMore?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  infiniteScrollProps?: Partial<InfiniteScroll>;
  itemListName: string;
}
export const MoviesList: FC<MoviesListProps> = ({
  moviesList,
  hasMore,
  fetchMovies,
  itemListName,
}) => {
  const theme = useTheme();
  const { cityId, getCity } = useCity();

  const triggerDataLayerEvent = (movies: MovieItemModel[]): void => {
    const data: ViewItemListModel = {
      event: EventTypes.VIEW_ITEM_LIST,
      ecommerce: {
        item_list_name: itemListName, // the category of listed movies eg: search
        items: movies,
      },
    };
    triggerGAEvent(data, true);
  };

  useEffect(() => {
    if (!moviesList || moviesList.length === 0) return;
    const movies: MovieItemModel[] = moviesList.map(
      ({ id, title, genres, cinemas, experiances }) => {
        return {
          item_id: id.toString(),
          item_name: title,
          item_brand: genres.map(genre => genre.name).join(','),
          item_category: experiances.map(experiance => experiance.name).join(','),
          item_category2: '',
          item_list_id: itemListName,
          item_list_name: itemListName,
          location: getCity(cityId)?.name,
          mall: cinemas.map(cinema => cinema.name).join(','),
          price: 0,
          quantity: 1,
        };
      }
    );
    triggerDataLayerEvent(movies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moviesList.length]);

  const triggerDataLayerSelectItemEvent = (index: number): void => {
    const { id, title, genres, experiances, cinemas } = moviesList[index];
    const data: SelectItemModel = {
      event: EventTypes.SELECT_ITEM,
      ecommerce: {
        item_list_name: itemListName,
        item_list_id: itemListName,
        items: [
          {
            item_id: id.toString(),
            item_name: title,
            item_brand: genres.map(genre => genre.name).join(','),
            item_category: experiances.map(experiance => experiance.name).join(','),
            item_category2: '',
            item_list_id: itemListName,
            item_list_name: itemListName,
            location: getCity(cityId)?.name,
            mall: cinemas.map(cinema => cinema.name).join(','),
            price: 0,
            quantity: 1,
          },
        ],
      },
    };
    triggerGAEvent(data, true);
  };

  return (
    <Box
      sx={{
        marginTop: pxToRem(48),
        marginBottom: pxToRem(30),
      }}
    >
      <InfiniteScroll
        dataLength={moviesList.length}
        next={fetchMovies}
        hasMore={hasMore}
        scrollThreshold={theme.breakpoints.down('extraLargeDesktop') ? '35%' : '70%'}
        loader={
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CustomizedCircularProgress size={40} thickness={4} />
          </Box>
        }
        style={{
          overflow: 'unset',
        }}
      >
        <Grid
          container
          columnSpacing={{
            mobile: '16px',
            tablet: pxToRem(57.33),
          }}
          rowSpacing={{
            mobile: '24px',
            tablet: pxToRem(78),
          }}
        >
          {moviesList?.map(
            (
              {
                uniqueName,
                title,
                movieLang,
                image,
                genres,
                bookmark,
                rate,
                isAdvancedBooking,
              },
              index
            ) => (
              <Zoom
                key={uniqueName}
                in={true}
                timeout={300}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <Grid
                  item
                  mobile={6}
                  largeMobile={4}
                  laptop={3}
                  onClick={() => triggerDataLayerSelectItemEvent(index)}
                >
                  <Poster
                    title={title}
                    language={movieLang}
                    img={image}
                    genres={genres.map(genre => genre.name)}
                    bookmark={bookmark}
                    isAdvancedBooking={isAdvancedBooking}
                    rate={rate}
                    link={`/movies/${uniqueName}`}
                  />
                </Grid>
              </Zoom>
            )
          )}
        </Grid>
      </InfiniteScroll>
    </Box>
  );
};

export default MoviesList;
